import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import {
  LogIn, User, KeyRound, Loader2, AlertTriangle, Shield,
  Mail, HelpCircle, MessageCircle, ArrowLeft, UserPlus, CheckCircle2, Download,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../api/auth";
import { downloadAccountBackup } from "../utils/accountBackup";
import { useContentObject } from "../hooks/useContentData";
import {
  sanitizeAccount,
  sanitizePassword,
  sanitizeSecretAnswer,
  sanitizeSecretQuestion,
  sanitizeEmail,
} from "../utils/inputSanitize";

const Tab = ({ active, children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-display border-b-2 transition ${
      active
        ? "text-[#d4a843] border-[#d4a843]"
        : "text-[#7e7768] border-transparent hover:text-[#b8b3a8]"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, icon: Icon, ...props }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4a843] mb-2 font-display">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a92]" />}
      <Input {...props} className={`${Icon ? "pl-10" : "pl-3"} h-11 bg-[#111118] border-[rgba(212,168,67,0.2)] focus:border-[#d4a843] text-[#f0ebe0] font-mono ${props.className || ""}`} />
    </div>
  </div>
);

const ErrorMsg = ({ msg }) => msg ? (
  <div className="flex items-start gap-3 p-3 bg-[#260d0d]/60 border border-[#b03030]/40 rounded-sm text-sm text-[#ef4444]">
    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{msg}</span>
  </div>
) : null;

// ============================================================
function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await login(account.trim(), password);
      navigate("/account/panel");
    } catch (err) {
      setError(err.message || "Error de login");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
      <Field label="Cuenta del Servidor" icon={User} value={account} onChange={(e) => setAccount(e.target.value)} placeholder="tu_cuenta_l2" required autoComplete="username" data-testid="login-account" />
      <Field label="Contraseña" icon={KeyRound} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" data-testid="login-password" />
      <ErrorMsg msg={error} />
      <button type="submit" disabled={submitting}
        className="btn-gold w-full py-3.5 text-xs rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="login-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Iniciando...</> : <><LogIn className="w-4 h-4" /> Iniciar Sesión</>}
      </button>
    </form>
  );
}

// ============================================================
function RegisterForm({ onSwitch }) {
  const { data: backupTexts } = useContentObject("account.backup");
  const [form, setForm] = useState({ account: "", password: "", email: "", question: "", answer: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { account, meta, password... }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      const res = await authApi.register({
        account: form.account.trim().toLowerCase(),
        password: form.password,
        email: form.email.trim(),
        question: form.question.trim(),
        answer: form.answer.trim(),
      });
      // descargar TXT inmediatamente
      downloadAccountBackup({
        action: "register",
        account: res.account,
        password: form.password,
        email: form.email.trim(),
        question: form.question.trim(),
        answer: form.answer.trim(),
        meta: res.meta,
        texts: backupTexts,
      });
      setSuccess(res);
    } catch (err) {
      setError(err.message || "No se pudo crear la cuenta");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="space-y-4 text-center" data-testid="register-success">
        <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
        <h3 className="text-lg text-white font-medium">¡Cuenta creada!</h3>
        <p className="text-sm text-[#b8b3a8]">
          Bienvenido <span className="text-[#d4a843] font-mono">{success.account}</span>. <br />
          Te descargamos un archivo TXT con toda la info de tu cuenta. <br />
          <span className="text-amber-400">Guardalo en un lugar seguro.</span>
        </p>
        <div className="flex items-center justify-center gap-2 text-xs text-[#7e7768] bg-black/30 border border-[#3a3328] rounded p-3">
          <Download className="w-3.5 h-3.5" /> Si no se descargó, revisá tus descargas o desactivá el bloqueador.
        </div>
        <button onClick={onSwitch} className="btn-gold w-full py-3 text-xs rounded-sm" data-testid="register-go-login">
          Iniciar Sesión Ahora
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
      <Field label="Usuario" icon={User}
        value={form.account}
        onChange={(e) => set("account", sanitizeAccount(e.target.value))}
        placeholder="alone123 (4-16 minusculas/numeros)"
        maxLength={16} required data-testid="register-account" />
      <Field label="Contraseña" icon={KeyRound} type="password" minLength={4} maxLength={16}
        value={form.password} onChange={(e) => set("password", sanitizePassword(e.target.value))}
        placeholder="4-16 letras y numeros" required data-testid="register-password" />
      <Field label="Email" icon={Mail} type="email"
        value={form.email} onChange={(e) => set("email", sanitizeEmail(e.target.value))}
        placeholder="tu@correo.com" required data-testid="register-email" />
      <Field label="Pregunta Secreta" icon={HelpCircle}
        value={form.question} onChange={(e) => set("question", sanitizeSecretQuestion(e.target.value))}
        placeholder="Ej: Como se llamaba mi primera mascota?"
        maxLength={200} required data-testid="register-question" />
      <Field label="Respuesta Secreta" icon={MessageCircle}
        value={form.answer} onChange={(e) => set("answer", sanitizeSecretAnswer(e.target.value))}
        placeholder="Solo letras y numeros"
        maxLength={100} required data-testid="register-answer" />
      <div className="text-[10px] text-[#7e7768] bg-black/20 border border-[#3a3328] rounded p-2.5">
        <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-400" />
        Guardá bien la pregunta y respuesta — son la única forma de recuperar tu contraseña.
      </div>
      <ErrorMsg msg={error} />
      <button type="submit" disabled={submitting}
        className="btn-gold w-full py-3.5 text-xs rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="register-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando...</> : <><UserPlus className="w-4 h-4" /> Crear Cuenta</>}
      </button>
    </form>
  );
}

// ============================================================
function ForgotForm({ onSwitch }) {
  const { data: backupTexts } = useContentObject("account.backup");
  const [step, setStep] = useState(1); // 1=email, 2=question+answer+newpass, 3=ok
  const [data, setData] = useState({ email: "", question: "", answer: "", new_password: "", account: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);

  const submitStep1 = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      const res = await authApi.recoverInit(data.email.trim());
      setData((d) => ({ ...d, question: res.question }));
      setStep(2);
    } catch (err) {
      setError(err.message || "Error");
    } finally { setSubmitting(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      const res = await authApi.recoverConfirm({
        email: data.email.trim(),
        answer: data.answer.trim(),
        new_password: data.new_password,
      });
      downloadAccountBackup({
        action: "recover",
        account: res.account,
        password: data.new_password,
        email: data.email.trim(),
        meta: res.meta,
        texts: backupTexts,
      });
      setMeta(res.meta);
      setStep(3);
    } catch (err) {
      setError(err.message || "Error");
    } finally { setSubmitting(false); }
  };

  if (step === 3) {
    return (
      <div className="space-y-4 text-center" data-testid="forgot-success">
        <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
        <h3 className="text-lg text-white font-medium">Contraseña restablecida</h3>
        <p className="text-sm text-[#b8b3a8]">
          Te descargamos un TXT con la nueva contraseña.<br />
          Ya podés iniciar sesión con <span className="text-[#d4a843] font-mono">{data.account}</span>.
        </p>
        <button onClick={onSwitch} className="btn-gold w-full py-3 text-xs rounded-sm">Iniciar Sesión</button>
      </div>
    );
  }

  return step === 1 ? (
    <form onSubmit={submitStep1} className="space-y-4" data-testid="forgot-form-step1">
      <p className="text-xs text-[#b8b3a8]">Ingresá el email con el que registraste tu cuenta:</p>
      <Field label="Email" icon={Mail} type="email"
        value={data.email} onChange={(e) => setData((d) => ({ ...d, email: sanitizeEmail(e.target.value) }))}
        placeholder="tu@correo.com" required data-testid="forgot-email" />
      <ErrorMsg msg={error} />
      <button type="submit" disabled={submitting}
        className="btn-gold w-full py-3 text-xs rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="forgot-step1-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Buscando...</> : "Continuar"}
      </button>
    </form>
  ) : (
    <form onSubmit={submitStep2} className="space-y-4" data-testid="forgot-form-step2">
      <div className="text-xs text-[#b8b3a8]">
        Responde tu pregunta secreta para restablecer la contraseña.
      </div>
      <div className="bg-black/30 border border-[#3a3328] rounded p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#7e7768] mb-1">Tu pregunta secreta</div>
        <div className="text-sm text-white">{data.question}</div>
      </div>
      <Field label="Respuesta Secreta" icon={MessageCircle}
        value={data.answer} onChange={(e) => setData((d) => ({ ...d, answer: sanitizeSecretAnswer(e.target.value) }))}
        placeholder="Solo letras y numeros" required data-testid="forgot-answer" />
      <Field label="Nueva Contraseña" icon={KeyRound} type="password" minLength={4} maxLength={16}
        value={data.new_password} onChange={(e) => setData((d) => ({ ...d, new_password: sanitizePassword(e.target.value) }))}
        placeholder="4-16 letras y numeros" required data-testid="forgot-new-password" />
      <ErrorMsg msg={error} />
      <button type="submit" disabled={submitting}
        className="btn-gold w-full py-3 text-xs rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="forgot-step2-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Cambiando...</> : "Restablecer Contraseña"}
      </button>
      <button type="button" onClick={() => { setStep(1); setError(""); }}
        className="text-xs text-[#7e7768] hover:text-[#d4a843] w-full text-center">
        ← Cambiar email
      </button>
    </form>
  );
}

// ============================================================
const LoginPage = () => {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("login"); // login | register | forgot

  if (!loading && user) return <Navigate to="/account/panel" replace />;

  return (
    <main className="bg-[#0a0a0d] min-h-screen flex items-center justify-center px-6 pt-32 pb-20">
      <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1585588985526-9459c1bb9e9e?auto=format&fit=crop&w=1920&q=80)", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0d]/60 via-[#0a0a0d]/85 to-[#0a0a0d] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#f5c542] via-[#d4a843] to-[#8b6914] mb-5">
            <div className="w-[58px] h-[58px] rounded-full bg-[#0a0a0d] flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#d4a843]" />
            </div>
          </div>
          <div className="ornament justify-center inline-flex"><span>Acceso al Reino</span></div>
          <h1 className="section-title mt-4 text-3xl">
            {tab === "login" ? "Panel de Cuenta" : tab === "register" ? "Crear Cuenta" : "Recuperar Acceso"}
          </h1>
        </div>

        <div className="glass rounded-sm p-7">
          <div className="flex border-b border-[#3a3328] mb-6 -mx-2">
            <Tab active={tab === "login"}    onClick={() => setTab("login")}    testId="tab-login">Login</Tab>
            <Tab active={tab === "register"} onClick={() => setTab("register")} testId="tab-register">Registro</Tab>
            <Tab active={tab === "forgot"}   onClick={() => setTab("forgot")}   testId="tab-forgot">Olvidé</Tab>
          </div>

          {tab === "login"    && <LoginForm />}
          {tab === "register" && <RegisterForm onSwitch={() => setTab("login")} />}
          {tab === "forgot"   && <ForgotForm  onSwitch={() => setTab("login")} />}
        </div>

        <p className="text-center mt-5 text-xs text-[#6f6c66]">
          <Link to="/" className="hover:text-[#d4a843] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Volver al sitio
          </Link>
        </p>
      </div>
    </main>
  );
};

export default LoginPage;
